/**
 * One conversation, and the four things a colleague can do to it.
 *
 * The action bar is driven by `mode` rather than by what the person looking at it
 * feels like doing, for the same reason the widget's presence rail is: the state
 * of a conversation is the one thing that must never be ambiguous, and both ends
 * are reading it off the same column.
 */
import { useEffect, useRef, useState } from 'react';
import { Loader2, LogOut, Send, UserPlus, XCircle } from 'lucide-react';
import { Button, cn } from '@respectly/ui';
import type { ChatSessionDetailDto, ChatTranscriptTurnDto } from '@respectly/shared-types';

export function SessionThread({
  detail,
  loading,
  acting,
  onClaim,
  onReply,
  onRelease,
  onClose,
}: {
  detail: ChatSessionDetailDto | null;
  loading: boolean;
  acting: boolean;
  onClaim: () => void;
  onReply: (text: string) => Promise<boolean>;
  onRelease: () => void;
  onClose: () => void;
}) {
  const [text, setText] = useState('');
  const endRef = useRef<HTMLDivElement | null>(null);
  const turnCount = detail?.transcript.length ?? 0;

  // Follow the conversation down as it grows, not on every re-render — a reply
  // that yanks the view while somebody is reading back is its own small bug.
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [turnCount, detail?.session.id]);

  if (loading && !detail) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-slate-500 dark:text-slate-400">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading the conversation&hellip;
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="flex flex-1 items-center justify-center px-6 text-center">
        <div>
          <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
            Pick a conversation
          </p>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            You can join any of them, not only the ones asking for a person.
          </p>
        </div>
      </div>
    );
  }

  const { session } = detail;
  const closed = session.mode === 'closed';
  const withAgent = session.mode === 'agent';

  const send = async (): Promise<void> => {
    const body = text.trim();
    if (!body || acting) return;
    // Cleared optimistically only after the server takes it: a reply that failed
    // silently and left an empty box is a message the visitor never got.
    const ok = await onReply(body);
    if (ok) setText('');
  };

  return (
    <>
      <div className="flex shrink-0 items-center gap-2 border-b border-slate-200 bg-white px-4 py-2 dark:border-slate-800 dark:bg-slate-900">
        <ModeBadge mode={session.mode} claimedBy={session.claimedByName} />
        <div className="flex-1" />
        {!closed && !withAgent && (
          <Button size="sm" onClick={onClaim} disabled={acting} className="h-8 gap-1.5">
            <UserPlus className="h-3.5 w-3.5" />
            Take this chat
          </Button>
        )}
        {!closed && withAgent && (
          <Button
            size="sm"
            variant="outline"
            onClick={onRelease}
            disabled={acting}
            className="h-8 gap-1.5"
          >
            <LogOut className="h-3.5 w-3.5" />
            Hand back
          </Button>
        )}
        {!closed && (
          <Button
            size="sm"
            variant="ghost"
            onClick={onClose}
            disabled={acting}
            className="h-8 gap-1.5 text-slate-500 hover:text-red-600 dark:text-slate-400"
          >
            <XCircle className="h-3.5 w-3.5" />
            End chat
          </Button>
        )}
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
        {detail.transcript.map((turn) => (
          <Turn key={turn.id} turn={turn} />
        ))}
        {detail.transcript.length === 0 && (
          <p className="py-8 text-center text-xs text-slate-400 dark:text-slate-500">
            The visitor has not said anything yet.
          </p>
        )}
        <div ref={endRef} />
      </div>

      <div className="shrink-0 border-t border-slate-200 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-900">
        {closed ? (
          <p className="text-center text-xs text-slate-400 dark:text-slate-500">
            This conversation has ended.
          </p>
        ) : (
          <div className="flex items-end gap-2">
            <textarea
              value={text}
              onChange={(e) => setText(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
              rows={2}
              placeholder={
                withAgent
                  ? 'Reply to the visitor…'
                  : 'Type to join — sending takes the chat over'
              }
              className={cn(
                'min-h-[44px] flex-1 resize-none rounded-xl border px-3 py-2 text-sm',
                'border-slate-200 bg-slate-50 text-slate-900 placeholder:text-slate-400',
                'focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500',
                'dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100'
              )}
            />
            <Button
              onClick={() => void send()}
              disabled={acting || !text.trim()}
              className="h-11 gap-1.5"
            >
              {acting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Send
            </Button>
          </div>
        )}
      </div>
    </>
  );
}

function ModeBadge({
  mode,
  claimedBy,
}: {
  mode: ChatSessionDetailDto['session']['mode'];
  claimedBy: string | null;
}) {
  const map = {
    waiting: ['Waiting for a person', 'bg-amber-50 text-amber-700 ring-amber-500/20 dark:bg-amber-950/40 dark:text-amber-400'],
    agent: [claimedBy ? `${claimedBy} is in this chat` : 'A colleague is in this chat', 'bg-teal-50 text-teal-700 ring-teal-500/20 dark:bg-teal-950/40 dark:text-teal-400'],
    bot: ['Assistant is answering', 'bg-slate-100 text-slate-600 ring-slate-400/20 dark:bg-slate-800 dark:text-slate-300'],
    closed: ['Ended', 'bg-slate-100 text-slate-500 ring-slate-400/20 dark:bg-slate-800 dark:text-slate-400'],
  } as const;
  const [label, classes] = map[mode] ?? map.bot;
  return (
    <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-medium ring-1', classes)}>
      {label}
    </span>
  );
}

/**
 * Three voices, three treatments.
 *
 * The visitor is right-aligned; the assistant and a colleague both sit left, but a
 * colleague's turn is named. Attribution is the whole reason `ChatMessage`
 * gained an `authorName` — a transcript that cannot say whether a person or a
 * model wrote a sentence is not a record of anything.
 */
function Turn({ turn }: { turn: ChatTranscriptTurnDto }) {
  if (turn.role === 'system') {
    return (
      <p className="text-center text-[11px] italic text-slate-400 dark:text-slate-500">
        {turn.content}
      </p>
    );
  }

  const isVisitor = turn.role === 'visitor';
  const isAgent = turn.role === 'agent';

  return (
    <div className={cn('flex', isVisitor && 'justify-end')}>
      <div className={cn('max-w-[80%]', isVisitor && 'text-right')}>
        {isAgent && (
          <p className="mb-0.5 text-[11px] font-medium text-teal-700 dark:text-teal-400">
            {turn.authorName ?? 'A colleague'}
          </p>
        )}
        <div
          className={cn(
            'inline-block whitespace-pre-wrap rounded-2xl px-3 py-2 text-sm',
            isVisitor
              ? 'bg-teal-600 text-white'
              : 'border border-slate-200 bg-white text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100'
          )}
        >
          {turn.content}
        </div>
        {turn.sources.length > 0 && (
          <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">
            Cited {turn.sources.map((s) => s.slug).join(', ')}
          </p>
        )}
        <p className="mt-0.5 text-[10px] text-slate-400 dark:text-slate-600">
          {new Date(turn.createdAt).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>
      </div>
    </div>
  );
}
